// Flower attachment (flower-attach-meter, playtest finding 7; corrected by wire-gathering-bit):
// a bee gathering from a patch should render visibly attached to it — snapped to a stable
// offset on the flower — rather than merely drawn nearby. Pure and Pixi-free, mirroring
// `ground-scatter.ts`/`season-ground.ts`'s seam shape; the GPU draw-position override lives in
// `renderer.ts`, exercised on-device only (repo convention D-086).
//
// The wire DOES carry an authoritative gather signal: `BeeRecord.gathering`, bit 6 of the packed
// facing/action byte (wire-gathering-bit), sourced from `waggle-sim`'s `GatheredNectar`
// decision-window flag. It reports receipt only, and is deliberately NOT the negation of the
// `not_gathering` B++ sense: that sense gained an opportunity gate (a bee in transit toward a
// sensed live patch reads `not_gathering == false` without having drawn anything, so
// `gathering` and `!not_gathering` legitimately disagree there). Attachment keys off
// `gathering`, plus the sim's own gather-source rule (SIM-RULES §5): the nearest pool > 0 patch
// within patch radius, ties broken by lowest patch id.
//
// It deliberately does NOT consult `ActionClass`. That enum is a four-value coarsening in which
// `CollectNectar`, `DropNectar`, `ReturnToHive`, `MoveTo`, `Patrol`, `Wander`, `Follow` and
// `Dance` all collapse to `Forage`, so the original `Forage`-plus-proximity heuristic also
// snapped bees merely walking, patrolling, wandering, following or dancing across a patch — the
// accepted false positive recorded as `docs/dev/deferred.md` D-147 and closed here. `gathering`
// already implies the bee drew nectar, which no non-foraging action can do, so the class adds
// nothing on top of it.

import { PATCH_RADIUS_U } from '../generated/sim-constants';

/** SIM-RULES §5 / DESIGN §14 patch interaction radius (world units): a bee within this
 * distance of a pool > 0 patch gathers from it. Not on the wire — the close-tier
 * `PatchRecord` carries only `id`/`position`/`pool` (wire-entity-detail) — so this mirrors
 * the seed balance constant client-side, the same way `ground-scatter.ts`'s
 * `DEFAULT_SCATTER_SEED` stands in for a value with no wire field.
 *
 * **fallback-permitted** (client-tick-rate-provenance / tick-rate-single-source task 5.5):
 * this value is on no wire at all — there is no handshake or trace envelope field to source it
 * from — so it is re-exported straight from the generated `sim-constants.ts` mirror (design E1)
 * rather than hand-written, so it can never independently drift from the balance preset's own
 * value. */
export { PATCH_RADIUS_U };

/** The world-space radius of the stable per-bee attachment ring around a flower — a cosmetic
 * constant, deliberately smaller than {@link PATCH_RADIUS_U} so an attached bee visually sits
 * on/against the flower sprite rather than out at the edge of its gather radius. */
export const ATTACHMENT_OFFSET_U = 6;

/** 2^32, the modulus of the unsigned 32-bit hash {@link attachmentOffset} mixes ids into —
 * dividing by it normalizes the hash to [0, 1) before mapping to an angle, so the angle
 * computation never multiplies by a huge integer (which would lose precision under `Math.cos`/
 * `Math.sin`'s internal argument reduction and could coincidentally collide two different
 * ids on the same reduced angle). */
const HASH_MODULUS = 4294967296;

/** A 32-bit avalanche finalizer (MurmurHash3 `fmix32`, mirroring `season-ground.ts`'s) mixing
 * both ids into one well-distributed hash — the same technique that spreads
 * `flowerVarietyFor`'s patch-id hash across its five varieties, applied here to spread the
 * attachment angle. */
function mixIds(beeId: number, patchId: number): number {
  let h = (Math.imul(beeId + 1, 0x9e3779b1) ^ Math.imul(patchId + 1, 0x85ebca77)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = (h ^ (h >>> 16)) >>> 0;
  return h;
}

/** The minimal per-bee shape the attachment rule needs: id (for the stable offset), its own
 * current world position, and the wire's authoritative {@link BeeRecord.gathering} flag. */
export interface AttachableBee {
  readonly id: number;
  readonly position: readonly [number, number];
  /** The wire's "this bee drew nectar in its current decision window" flag
   * (wire-gathering-bit) — never `action === ActionClass.Forage`, which is true of seven other
   * actions besides. */
  readonly gathering: boolean;
}

/** The minimal per-patch shape the attachment rule needs. */
export interface AttachablePatch {
  readonly id: number;
  readonly position: readonly [number, number];
  readonly pool: number;
}

/**
 * The nearest pool > 0 patch within {@link PATCH_RADIUS_U} of `bee`'s own position, tie broken
 * by lowest patch id — mirroring the sim's own `collect_nectar` source selection (SIM-RULES
 * §5) — or `undefined` when no patch qualifies or the bee is not {@link AttachableBee.gathering}.
 *
 * Since `wire-gathering-bit` this closes the ACTION-CLASS false positives exactly: `gathering`
 * is the sim's own `GatheredNectar` window flag, so a bee reaches this rule only if it
 * genuinely drew nectar THAT DECISION WINDOW, closing D-147 (a bee walking, patrolling,
 * wandering, following or dancing across a patch no longer attaches, nor does one still
 * heading toward a patch under `collect_nectar`, nor one capped out of spare capacity, nor one
 * that lost a `max_bees_per_patch` claimant contest).
 *
 * **What it still cannot do (review finding, `docs/dev/deferred.md`): identify WHICH source
 * the bee actually drew from.** `gathering` only says SOME source (a patch, a ground spill, or
 * the hive pool — the sim picks the nearest across all three) granted nectar this window; this
 * function then guesses "the nearest live patch in radius, if any" rather than reading the
 * sim's own choice back off the wire (no per-bee source id is on the wire at all). A bee that
 * actually drew from a nearer SPILL while ALSO sitting within {@link PATCH_RADIUS_U} of an
 * unrelated live patch therefore still misattaches to that patch — this function cannot tell
 * the two cases apart with the fields it has. Closing this fully needs the sim's selected
 * source kind/id carried through the wire record, which is a `waggle-sim`/`waggle-proto`
 * change, not a client one.
 *
 * It does not restrict to a single claimant either: under the seed preset's uncapped
 * `max_bees_per_patch` (`0`, forage-cap-uncapped) or any cap above 1, every bee gathering near
 * a patch attaches to it, each to its own stable offset (see {@link attachmentOffset}) — a
 * best-effort picture of contention, not a guaranteed-exact one.
 *
 * A gathering bee with no pool > 0 patch in reach at all — it drew from a ground spill or the
 * hive pool with no live patch nearby to be mistaken for — correctly yields `undefined` and
 * renders at its own position; that narrower case IS exact.
 */
export function nearestGatherablePatch(
  bee: AttachableBee,
  patches: Iterable<AttachablePatch>,
): AttachablePatch | undefined {
  if (!bee.gathering) {
    return undefined;
  }
  const [bx, by] = bee.position;
  const radiusSq = PATCH_RADIUS_U * PATCH_RADIUS_U;
  let best: AttachablePatch | undefined;
  let bestDistSq = Infinity;
  for (const patch of patches) {
    if (patch.pool <= 0) continue;
    const dx = patch.position[0] - bx;
    const dy = patch.position[1] - by;
    const distSq = dx * dx + dy * dy;
    if (distSq > radiusSq) continue;
    if (best === undefined || distSq < bestDistSq || (distSq === bestDistSq && patch.id < best.id)) {
      best = patch;
      bestDistSq = distSq;
    }
  }
  return best;
}

/**
 * A stable, deterministic world-space offset for `beeId` on patch `patchId` (flower-attach-
 * meter): combines both ids so two different bees attached to the SAME patch tend to land at
 * different points around it (the "several bees qualify" case — {@link nearestGatherablePatch}
 * does not restrict to one claimant), spread around the ring by a well-distributed id hash.
 * Pure — the same `(beeId, patchId)` pair always yields the same offset, independent of
 * frame, render order, or how many other bees are also attached this frame — never
 * `Math.random` (the repo's seeded-cosmetic convention, `ground-scatter.ts`'s `splitmix32`).
 *
 * **Deliberately NOT collision-proof** (review finding closed by revising the earlier
 * "never stack" claim rather than chasing an unsatisfiable one, `docs/dev/deferred.md`): this
 * function is a pure map from `(beeId, patchId)` alone, so a bee's offset never moves when a
 * SIBLING bee joins or leaves the same patch — the ring stays visually stable as contention
 * changes tick to tick, which is the whole point of keying it this way rather than, say,
 * evenly spacing the CURRENT claimants around the ring (which would jitter every existing
 * claimant's dot each time the group's size changed). The cost of that stability is
 * unavoidable: {@link ATTACHMENT_OFFSET_U}'s ring is a handful of screen pixels wide once
 * projected at the close tier's own zoom floor (`defaultTierForZoom`'s ~0.9 scale), so even
 * TWO independently-hashed ids can — occasionally, not systematically — round to the same
 * integer pixel after `worldToScreen`'s squash + the renderer's rounding (measured: bee ids 4
 * and 5 on patch 9 do, at camera scale 1). No choice of hash removes this for an unbounded
 * number of claimants at an unbounded range of zoom without EITHER growing the ring (still
 * bounded by {@link PATCH_RADIUS_U}) or making the offset depend on the other claimants (which
 * would break the stability this function exists to provide) — so a rare coincidental overlap
 * is an accepted trade-off, not a silent gap: `flower-attach.test.ts` tests for it by name.
 */
export function attachmentOffset(beeId: number, patchId: number): readonly [number, number] {
  const angle = (mixIds(beeId, patchId) / HASH_MODULUS) * 2 * Math.PI;
  return [Math.cos(angle) * ATTACHMENT_OFFSET_U, Math.sin(angle) * ATTACHMENT_OFFSET_U];
}

/**
 * The world-space point `bee` should render at when attached to a flower (flower-attach-
 * meter): the nearest gatherable patch's position plus this bee's stable
 * {@link attachmentOffset}, or `undefined` when no patch qualifies — the caller then falls
 * back to the bee's own (interpolated) position, unchanged.
 */
export function attachedFlowerAnchor(
  bee: AttachableBee,
  patches: Iterable<AttachablePatch>,
): readonly [number, number] | undefined {
  const patch = nearestGatherablePatch(bee, patches);
  if (patch === undefined) {
    return undefined;
  }
  const [ox, oy] = attachmentOffset(bee.id, patch.id);
  return [patch.position[0] + ox, patch.position[1] + oy];
}

/**
 * {@link attachedFlowerAnchor} for every bee in `bees` at once, keyed by bee id (flower-
 * attach-meter review finding: computing this per-bee, per-RAF-frame inside `buildDrawables`
 * — called every animation frame, not only when a new close-tier snapshot actually arrives —
 * makes the O(bees × patches) nearest-patch scan run at RAF frequency (up to ~60 Hz) instead
 * of the streamed close-tier cadence (design "Brain decision interval"/PROTOCOL §4, ~10 Hz)).
 *
 * This function does the SAME O(bees × patches) work `attachedFlowerAnchor` would if called
 * once per bee, but is meant to be called exactly ONCE per accepted close-tier frame — the
 * production caller (`CanvasRenderer.render`) computes it there, alongside the other
 * once-per-accepted-close-frame ingestion (`FacingDeriver.onCloseFrame`,
 * `PresentationClock.ingest`, `DanceTracker.onCloseFrame` — the same "derived once per
 * accepted frame, reused every RAF" pattern architecture-review A2 already established for
 * dances), and every RAF's `buildDrawables` call is handed the resulting map's lookup instead
 * of recomputing it. A bee absent from the returned map is not attached (renders at its own
 * position); `attachedFlowerAnchor` itself is unchanged and still used as the fallback for
 * call sites that pass no precomputed lookup (every pure-function test in
 * `renderer.test.ts`/`flower-attach.test.ts`), so this is additive, not a replacement.
 */
export function attachmentsForFrame(
  bees: Iterable<AttachableBee>,
  patches: Iterable<AttachablePatch>,
): ReadonlyMap<number, readonly [number, number]> {
  const patchList = [...patches];
  const out = new Map<number, readonly [number, number]>();
  for (const bee of bees) {
    const anchor = attachedFlowerAnchor(bee, patchList);
    if (anchor !== undefined) {
      out.set(bee.id, anchor);
    }
  }
  return out;
}
